<script setup lang="ts">

import { ref, watch } from 'vue';
import { NumericValidator, makeInComponentValidationWrapper } from './inputValidation'

const props = defineProps<{
  validator: NumericValidator,
}>();

const emit = defineEmits(['blur'])
const numericValue = defineModel<number>({default: 0});
const root = ref<HTMLInputElement|undefined>(undefined);

// Event Flow
// ----------
//
//          OUTSIDE
//           |  ^
//  v-model  |  | v-model
//           v  |
//          MODEL (numericValue)
//           |  ^
//   (A)     |  |  (B)
// on change |  | watch, parse
// & format  |  | & reformat
//           |  | on change
//           v  |
//          ELEMENT (root)

const validate = makeInComponentValidationWrapper<number>(
  root, numericValue, props.validator);

// (A) numericValue -> root
watch(numericValue, (newValue: number, _oldValue: number) => {
  setRootValue(newValue);
  validate(undefined);
});

// (B) root -> numericValue
function onRootChanged(evt: Event) {
  const incoming = root.value?.value ?? '0';
  const s = incoming.trim().replaceAll(',', '');
  const n = parseFloat(s);

  if (numericValue.value != n) {
    numericValue.value = n;
    emit('blur', evt);
  }

  setRootValue(n);
};

function onKeyUp(evt: KeyboardEvent) {
  const multipliers: Record<string, number> = {
    'k': 1000,
    'K': 1000,
  };
  const multiplier = multipliers[evt.key];

  if (multiplier) {
    const raw = root.value?.value;
    if (raw && raw.endsWith(evt.key)) {
      const s = raw.substring(0, raw.length - 1).replaceAll(',', '');
      const n = parseFloat(s);
      if (!Number.isNaN(n)) {
        setRootValue(n * multiplier);
        root.value?.blur();
      }
    }
  }
}

function setRootValue(nValue: number) {
  function commarize(units: number, change: number): string {
    console.assert(
      units === Math.floor(units),
      `units is ${units} but should be an integer.`,
    );

    console.assert(
      Math.abs(change) < 1,
      `change is ${change} but should be less than 1.`,
    );

    const changeMillis = Math.floor(change * 1000)
    const changeCentis = Math.round(changeMillis/10);
    const sChangeCentis = (
      (changeCentis < 10) ?
        '0' :
        ''
    ) + changeCentis.toString();

    const sChange = (change !== 0 && changeCentis !== 0) ?
      ('.' + sChangeCentis.replace(/^0\./, '')) :
      '';

    const sUnits = units.toString();

    // Match 1-3 characters that are followed by groups of exactly 3 characters 
    // until the end of the string.
    const groups = sUnits.match(/.{1,3}(?=(.{3})*$)/g) || [];

    const result = groups.join(',') + sChange; 
    return result;
  }

  function setStringValue(value: string) {
    if (root.value) {
      root.value.value = value;
      root.value.innerText = value;
    }
  }

  if (nValue == 0) {
    setStringValue('0');
    return;
  }

  if (Number.isNaN(nValue)) {
    setStringValue('0');
    return;
  }

  if (!nValue) {
    setStringValue('?');
    return;
  }

  const units: number = Math.floor(nValue);
  const change: number = nValue - units;
  let s = commarize(units, change);
  setStringValue(s);
}


</script>

<template>
  <input
    class="fin-input-numeric2"
    ref="root"
    type="text"
    @blur="onRootChanged"
    @keyup="onKeyUp"
  />
  </input>
</template>

<style scoped>

input {
  width: 110px;
}

</style>
